import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import ScheduleIcon from '@mui/icons-material/Schedule';
import KeyIcon from '@mui/icons-material/Key';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { getTokens, toggleTokenStatus, getTokenValue, getTokenRequestsCount, getTokenFilterUsers } from '../api/tokens';
import { Card, Button, Autocomplete, TextField, Switch, Tabs, Tab } from '../components/ui-kit';
import TokenRequests from './TokenRequests';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import SortableTableHead from '../components/SortableTableHead';
import SearchInput from '../components/SearchInput';
import TokenFormDialog from '../components/TokenFormDialog';
import TokenViewDialog from '../components/TokenViewDialog';
import PermissionFiltersPanel from '../components/PermissionFiltersPanel';

const TOKEN_STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Revoked' },
];

const getActiveTokenFilterCount = (filters) => [
  filters.services?.length > 0 ? 1 : 0,
  filters.resources?.length > 0 ? 1 : 0,
  filters.actions?.length > 0 ? 1 : 0,
  filters.status !== 'all' ? 1 : 0,
  filters.ownerUserId ? 1 : 0,
  filters.createdBy ? 1 : 0,
  filters.expiresBefore ? 1 : 0,
  filters.expiredOnly ? 1 : 0,
].reduce((a, b) => a + b, 0);

// Column definitions for the tokens table
const TOKEN_TABLE_COLUMNS = [
  {
    field: 'tokenName',
    headerName: 'Name',
    sortable: true,
    minWidth: 150,
    width: '20%'
  },
  {
    field: 'tokenPrefix',
    headerName: 'Token Prefix',
    sortable: false,
    minWidth: 150,
    width: '15%'
  },
  {
    field: 'ownerUserId',
    headerName: 'Owner',
    sortable: false,
    minWidth: 160,
    width: '13%'
  },
  {
    field: 'createdBy',
    headerName: 'Created By',
    sortable: false,
    minWidth: 160,
    width: '13%'
  },
  {
    field: 'permissions',
    headerName: 'Permissions',
    sortable: false,
    minWidth: 110,
    width: '10%'
  },
  {
    field: 'expiresAt',
    headerName: 'Expires',
    sortable: true,
    minWidth: 130,
    width: '12%'
  },
  {
    field: 'isActive',
    headerName: 'Status',
    sortable: false,
    align: 'center',
    minWidth: 90,
    width: '8%'
  },
  {
    field: 'actions',
    headerName: 'Actions',
    sortable: false,
    align: 'center',
    minWidth: 110,
    width: '9%'
  },
];

function TokensList() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();

  const canCreate = hasPermission('auth-service', 'api-token', 'create');

  const [activeTab, setActiveTab] = useState(0);

  const { data: requestsCountData } = useQuery({
    queryKey: ['token-requests-count'],
    queryFn: getTokenRequestsCount,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const requestsCount = requestsCountData?.count || 0;

  const hasUpdateAll = hasPermission('auth-service', 'api-token', 'update');
  const hasUpdateOwn = hasPermission('auth-service', 'api-token', 'update-own');
  const hasViewValueAll = hasPermission('auth-service', 'api-token', 'view-value');
  const hasViewValueOwn = hasPermission('auth-service', 'api-token', 'view-value-own');
  const hasReadAll = hasPermission('auth-service', 'api-token', 'read');
  const hasReadOwn = hasPermission('auth-service', 'api-token', 'read-own');

  const canUpdate = hasUpdateAll || hasUpdateOwn;
  const canRead = hasReadAll || hasReadOwn;
  const hasActions = canRead || canUpdate;

  // Returns true if current user is owner or creator of the token
  const isMyToken = (token) => {
    if (!user?.id) return false;
    return token.ownerUserId?._id === user.id || token.createdBy?._id === user.id;
  };

  // Per-token checks: full permission OR (own-only permission AND I own this token)
  const canUpdateToken = (token) => hasUpdateAll || (hasUpdateOwn && isMyToken(token));
  const canViewValueToken = (token) => hasViewValueAll || (hasViewValueOwn && isMyToken(token));
  const canReadToken = (token) => hasReadAll || (hasReadOwn && isMyToken(token));

  const visibleColumns = TOKEN_TABLE_COLUMNS.filter(
    col => col.field !== 'actions' || hasActions
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [permissionFilters, setPermissionFilters] = useState({
    services: [],
    resources: [],
    actions: [],
    status: 'all',
    ownerUserId: '',
    createdBy: '',
    expiresBefore: '',
    expiredOnly: false,
  });
  const [nameFilter, setNameFilter] = useState('');
  const activeFilterCount = getActiveTokenFilterCount(permissionFilters);

  // Load only users who actually own or created a token (for Owner / Created By pickers)
  const { data: usersData } = useQuery({
    queryKey: ['tokens', 'filter-users'],
    queryFn: () => getTokenFilterUsers(),
    enabled: isFiltersExpanded,
    staleTime: 5 * 60 * 1000,
  });
  const users = useMemo(() => usersData?.data || [], [usersData]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [permissionFilters, nameFilter]);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null);
  const [newlyCreatedToken, setNewlyCreatedToken] = useState(null);

  const tokenFilters = {
    services: permissionFilters.services,
    resources: permissionFilters.resources,
    actions: permissionFilters.actions,
    isActive:
      permissionFilters.status === 'active'
        ? true
        : permissionFilters.status === 'inactive'
          ? false
          : undefined,
    ownerUserId: permissionFilters.ownerUserId || undefined,
    createdBy: permissionFilters.createdBy || undefined,
    expiresBefore: permissionFilters.expiresBefore || undefined,
    expiredOnly: permissionFilters.expiredOnly || undefined,
    name: nameFilter || undefined,
    sortField,
    sortDirection,
  };

  // Fetch tokens
  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ['tokens', page + 1, rowsPerPage, tokenFilters],
    queryFn: () => getTokens(page + 1, rowsPerPage, tokenFilters),
    keepPreviousData: true,
  });

  // Toggle token status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: toggleTokenStatus,
    onSuccess: (response) => {
      queryClient.invalidateQueries(['tokens']);
      const message = response.message || (response.data?.isActive ? 'Token activated successfully' : 'Token deactivated successfully');
      toast.success(message);
    },
    onError: (error) => {
      toast.error(`Failed to toggle token status: ${error.response?.data?.message || error.message}`);
    },
  });

  const tokens = data?.data || [];
  const totalCount = data?.count || 0;


  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleViewToken = (token) => {
    setSelectedToken(token);
    setViewDialogOpen(true);
  };

  const handleEditToken = (token) => {
    setSelectedToken(token);
    setEditDialogOpen(true);
  };

  const handleToggleStatus = (token) => {
    toggleStatusMutation.mutate(token.id);
  };

  const handleCopyTokenPrefix = (tokenPrefix) => {
    navigator.clipboard.writeText(tokenPrefix);
    toast.success('Token prefix copied to clipboard');
  };

  const handleCopyFullToken = async (tokenId) => {
    try {
      const response = await getTokenValue(tokenId);
      if (response.success && response.data.tokenValue) {
        navigator.clipboard.writeText(response.data.tokenValue);
        toast.success('Full token copied to clipboard');
      } else {
        toast.error('Token value not available');
      }
    } catch (error) {
      console.error('Error copying token:', error);
      const message = error.response?.data?.message || 'Failed to copy token';
      toast.error(message);
    }
  };

  const selectedOwner = users.find((u) => u.id === permissionFilters.ownerUserId) || null;
  const selectedCreator = users.find((u) => u.id === permissionFilters.createdBy) || null;
  const userOptionLabel = (u) => u?.fullName || u?.email || '';

  const extraTokenFilters = useMemo(
    () => (
      <>
        <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Owner
          </Typography>
          <Autocomplete
            size="small"
            options={users}
            value={selectedOwner}
            getOptionLabel={userOptionLabel}
            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
            onChange={(_e, next) =>
              setPermissionFilters((f) => ({ ...f, ownerUserId: next?.id || '' }))
            }
            renderInput={(params) => (
              <TextField {...params} placeholder="Any owner" variant="outlined" />
            )}
          />
        </Box>

        <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Created By
          </Typography>
          <Autocomplete
            size="small"
            options={users}
            value={selectedCreator}
            getOptionLabel={userOptionLabel}
            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
            onChange={(_e, next) =>
              setPermissionFilters((f) => ({ ...f, createdBy: next?.id || '' }))
            }
            renderInput={(params) => (
              <TextField {...params} placeholder="Any creator" variant="outlined" />
            )}
          />
        </Box>

        <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Expires before
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={permissionFilters.expiresBefore ? dayjs(permissionFilters.expiresBefore) : null}
              onChange={(newValue) => {
                if (!newValue) {
                  setPermissionFilters((f) => ({ ...f, expiresBefore: '' }));
                  return;
                }
                if (!newValue.isValid()) return;
                setPermissionFilters((f) => ({ ...f, expiresBefore: newValue.format('YYYY-MM-DD') }));
              }}
              slotProps={{
                textField: { size: 'small', fullWidth: true },
              }}
            />
          </LocalizationProvider>
        </Box>

        <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Expired only
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: 40 }}>
            <Switch
              size="small"
              checked={!!permissionFilters.expiredOnly}
              onChange={(e) =>
                setPermissionFilters((f) => ({ ...f, expiredOnly: e.target.checked }))
              }
            />
            <Typography variant="body2">
              {permissionFilters.expiredOnly ? 'Yes' : 'No'}
            </Typography>
          </Box>
        </Box>
      </>
    ),
    [users, selectedOwner, selectedCreator, permissionFilters.expiresBefore, permissionFilters.expiredOnly]
  );

  const handleClearAllFilters = () => {
    setNameFilter('');
    setPermissionFilters({
      services: [],
      resources: [],
      actions: [],
      status: 'all',
      ownerUserId: '',
      createdBy: '',
      expiresBefore: '',
      expiredOnly: false,
    });
  };

  const handleCreateSuccess = (createdToken, fullToken) => {
    setNewlyCreatedToken({ ...createdToken, token: fullToken });
    setCreateDialogOpen(false);
    setViewDialogOpen(true);
    queryClient.invalidateQueries(['tokens']);
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const isExpired = (token) => {
    if (typeof token.isExpired === 'boolean') return token.isExpired;
    if (!token.expiresAt) return false;
    return new Date(token.expiresAt) < new Date();
  };

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          Error loading tokens: {error?.response?.data?.message || error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <KeyIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            API Tokens
          </Typography>
        </Box>
        {canCreate && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Token
          </Button>
        )}
      </Box>

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab label="All Tokens" />
        <Tab
          label={
            <Badge badgeContent={requestsCount || null} color="warning" sx={{ pr: requestsCount ? 1.5 : 0 }}>
              Requests
            </Badge>
          }
        />
      </Tabs>

      {activeTab === 1 ? (
        <TokenRequests />
      ) : (
      <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <SearchInput
            placeholder="Search tokens by name..."
            onSearch={setNameFilter}
            defaultValue={nameFilter}
            fullWidth
            size="small"
          />
        </Box>
        <Tooltip title="Filter tokens">
          <IconButton
            onClick={() => setIsFiltersExpanded((v) => !v)}
            color={activeFilterCount > 0 ? 'primary' : 'default'}
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              transition: 'all 0.3s ease',
              boxShadow: activeFilterCount > 0 ? '0 0 0 2px rgba(25, 118, 210, 0.2)' : 'none',
              '&:hover': {
                backgroundColor: activeFilterCount > 0
                  ? 'rgba(25, 118, 210, 0.1)'
                  : 'rgba(0, 0, 0, 0.04)',
                transform: 'scale(1.05)',
              },
            }}
          >
            <Badge badgeContent={activeFilterCount} color="primary">
              <FilterListIcon />
            </Badge>
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ mb: 2 }}>
        <PermissionFiltersPanel
          title="Filter API Tokens"
          value={permissionFilters}
          onChange={setPermissionFilters}
          statusOptions={TOKEN_STATUS_OPTIONS}
          extraFilters={extraTokenFilters}
          isExpanded={isFiltersExpanded}
          onToggleExpand={() => setIsFiltersExpanded((v) => !v)}
          onClearAll={handleClearAllFilters}
        />
      </Box>

      <Card>
        <TableContainer sx={{ position: 'relative' }}>
          {isFetching && !isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 1,
              }}
            >
              <LinearProgress />
            </Box>
          )}
          <Table sx={{ opacity: isFetching && !isLoading ? 0.6 : 1, transition: 'opacity 0.15s ease' }}>
            <SortableTableHead
              columns={visibleColumns}
              sortConfig={{ field: sortField, direction: sortDirection }}
              handleSortRequest={handleRequestSort}
            />
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : tokens.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {nameFilter || activeFilterCount > 0
                        ? 'No tokens match your search or filters.'
                        : 'No tokens found. Create your first API token to get started.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tokens.map((token) => (
                  <TableRow key={token.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {token.tokenName}
                      </Typography>
                      {token.description && (
                        <Typography variant="caption" color="text.secondary">
                          {token.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontFamily="monospace">
                          {token.tokenPrefix}
                        </Typography>
                        <Tooltip title="Copy prefix">
                          <IconButton
                            size="small"
                            onClick={() => handleCopyTokenPrefix(token.tokenPrefix)}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canViewValueToken(token) && (
                          <Tooltip title="Copy full token">
                            <IconButton
                              size="small"
                              onClick={() => handleCopyFullToken(token.id)}
                              color="primary"
                            >
                              <KeyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        { token.ownerUserId?.fullName || token.ownerUserId?.email || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        { token.createdBy?.fullName || token.createdBy?.email || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${token.permissions?.length || 0} permissions`}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography
                          variant="body2"
                          color={isExpired(token) ? 'error' : 'text.primary'}
                        >
                          {formatDate(token.expiresAt)}
                        </Typography>
                        {isExpired(token) && (
                          <Typography variant="caption" color="error">
                            Expired
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={!token.isActive ? <BlockIcon /> : isExpired(token) ? <ScheduleIcon /> : <CheckCircleIcon />}
                        label={!token.isActive ? 'Revoked' : isExpired(token) ? 'Expired' : 'Active'}
                        color={!token.isActive ? 'error' : isExpired(token) ? 'warning' : 'success'}
                        size="small"
                        onClick={canUpdateToken(token) ? () => handleToggleStatus(token) : undefined}
                        sx={{ cursor: canUpdateToken(token) ? 'pointer' : 'default' }}
                      />
                    </TableCell>
                    {hasActions && (
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          {canReadToken(token) && (
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleViewToken(token)}
                                color="primary"
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canUpdateToken(token) && (
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEditToken(token)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Card>

      {/* Create Token Dialog */}
      <TokenFormDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Edit Token Dialog */}
      {selectedToken && (
        <TokenFormDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedToken(null);
          }}
          token={selectedToken}
          onSuccess={() => {
            setEditDialogOpen(false);
            setSelectedToken(null);
            queryClient.invalidateQueries(['tokens']);
          }}
        />
      )}

      {/* View Token Dialog */}
      {(selectedToken || newlyCreatedToken) && (
        <TokenViewDialog
          open={viewDialogOpen}
          onClose={() => {
            setViewDialogOpen(false);
            setSelectedToken(null);
            setNewlyCreatedToken(null);
          }}
          token={selectedToken || newlyCreatedToken}
          fullToken={newlyCreatedToken?.token}
          canViewValue={canViewValueToken(selectedToken || newlyCreatedToken)}
        />
      )}
      </>
      )}
    </Box>
  );
}

export default TokensList;
